import React, { useState } from 'react';
import { printHtmlSilently } from '../lib/silentPrint';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Scale, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => new Date().toISOString().substring(0, 10);
const offsetStr = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().substring(0, 10); };
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().substring(0, 10); };
const firstOfYear = () => { const d = new Date(); return new Date(d.getFullYear(), 0, 1).toISOString().substring(0, 10); };
const fmtDateBR = (isoDate) => { if (!isoDate) return ''; const [y, m, d] = isoDate.split('-'); return `${d}/${m}/${y}`; };
const fmtMoney = (v) => Number(v || 0).toFixed(2).replace('.', ',');
const fmtKg = (v) => `${Number(v || 0).toFixed(3).replace('.', ',')} kg`;

export const WeightSalesReport = () => {
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const applyPreset = (preset) => {
    const today = todayStr();
    if (preset === 'today') { setStart(today); setEnd(today); }
    else if (preset === 'yesterday') { const y = offsetStr(-1); setStart(y); setEnd(y); }
    else if (preset === '7d') { setStart(offsetStr(-6)); setEnd(today); }
    else if (preset === 'month') { setStart(firstOfMonth()); setEnd(today); }
    else if (preset === 'year') { setStart(firstOfYear()); setEnd(today); }
  };

  const handleSearch = async () => {
    setLoading(true); setError(''); setReport(null);
    try {
      const params = new URLSearchParams({ start_date: start, end_date: end });
      const { data } = await axios.get(`${API}/sales/weight-report?${params.toString()}`, { withCredentials: true });
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!report) return;
    const rows = (report.products || []).map((p, i) => `
      <tr>
        <td>${i + 1}. ${escapeHtml((p.name || '').toUpperCase())}</td>
        <td class="right">${fmtKg(p.total_kg)}</td>
        <td class="right">R$ ${fmtMoney(p.total_value)}</td>
      </tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Relatório por Peso</title>
<style>@page{size:80mm auto;margin:3mm;}body{font-family:'Courier New',monospace;font-size:11px;color:#000;}
.center{text-align:center;}.right{text-align:right;}.bold{font-weight:700;}table{width:100%;border-collapse:collapse;}
td{padding:1px 0;font-size:11px;}hr{border:none;border-top:1px dashed #000;margin:4px 0;}.lbl{font-size:10px;text-transform:uppercase;}</style>
</head><body>
<div class="center bold">================================</div>
<div class="center bold">   RELATORIO POR PESO</div>
<div class="center bold">================================</div>
<div><span class="lbl">Periodo:</span> ${fmtDateBR(report.period.start_date)} a ${fmtDateBR(report.period.end_date)}</div>
<div><span class="lbl">Emitido:</span> ${new Date().toLocaleString('pt-BR')}</div>
<hr/>
<table>${rows}</table>
<hr/>
<div class="row bold" style="display:flex;justify-content:space-between;"><span>TOTAL KG:</span><span>${fmtKg(report.total_kg)}</span></div>
<div class="row bold" style="display:flex;justify-content:space-between;"><span>TOTAL R$:</span><span>R$ ${fmtMoney(report.total_value)}</span></div>
<hr/><div class="center">Smart Flow POS</div><br/><br/>
</body></html>`;
    printHtmlSilently(html);
  };

  const products = report?.products || [];

  return (
    <div data-testid="weight-report">
      <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Scale size={22} /> Relatório por Peso</h2>
      <p className="text-sm text-slate-600 mb-4">Total em kg vendido por produto pesado (unidade kg) no período.</p>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Período</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={() => applyPreset('today')} data-testid="w-preset-today">Hoje</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('yesterday')} data-testid="w-preset-yesterday">Ontem</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('7d')} data-testid="w-preset-7d">Últimos 7 dias</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('month')} data-testid="w-preset-month">Este mês</Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset('year')} data-testid="w-preset-year">Este ano</Button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs text-slate-500">De</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="w-date-start" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Até</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="w-date-end" />
          </div>
        </div>
        <Button onClick={handleSearch} disabled={loading} className="w-full mt-4 bg-blue-600 hover:bg-blue-700" data-testid="w-search-btn">
          {loading ? 'Buscando...' : 'Gerar Relatório'}
        </Button>
      </div>

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      {report && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="weight-report-result">
          <p className="text-sm text-slate-600 mb-4">
            Período: <strong>{fmtDateBR(report.period.start_date)}</strong> até <strong>{fmtDateBR(report.period.end_date)}</strong>
          </p>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Total Vendido (kg)</p>
              <p className="text-3xl font-black text-emerald-700 mt-1" data-testid="w-total-kg">{fmtKg(report.total_kg)}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-yellow-700">Valor Total</p>
              <p className="text-3xl font-black text-yellow-700 mt-1" data-testid="w-total-value">R$ {fmtMoney(report.total_value)}</p>
            </div>
          </div>

          {products.length === 0 ? (
            <Alert className="bg-yellow-50 border-yellow-300">
              <AlertDescription className="text-yellow-900">Nenhum produto pesado vendido no período selecionado.</AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Produto</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Total (kg)</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Valor</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-500">R$/kg médio</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Vendas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((p, i) => (
                    <tr key={p.product_id} className="hover:bg-slate-50" data-testid={`weight-row-${i}`}>
                      <td className="px-3 py-2 text-sm">
                        <span className="font-medium">{p.name}</span>
                        {p.code && <span className="text-xs text-slate-400 ml-2 font-mono">{p.code}</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-bold text-emerald-700">{fmtKg(p.total_kg)}</td>
                      <td className="px-3 py-2 text-right text-sm font-bold">R$ {fmtMoney(p.total_value)}</td>
                      <td className="px-3 py-2 text-right text-sm text-slate-600">R$ {fmtMoney(p.avg_price_per_kg)}</td>
                      <td className="px-3 py-2 text-right text-sm text-slate-600">{p.sales_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {products.length > 0 && (
            <Button onClick={handlePrint} className="w-full mt-6 bg-slate-900 hover:bg-slate-800" data-testid="w-print-btn">
              <Printer size={16} className="mr-2" /> Imprimir Cupom (80mm)
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
